import {
  fetchResortAccommodations,
  fetchResortRelated,
  fetchResortShortInfo,
  fetchResortShortWeatherReport,
} from 'api/resort/static';
import { IResortPageInfo, IResortPathInfo } from 'api/resort/types';
import { AxiosResponse } from 'axios';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Routes } from 'routes';
import { getResortSlug } from 'util/memcache';
import { fetchResortMeta } from 'api/region/static';
import {
  NotFoundError, assertShouldBeNot404, wrapServerSideProps, wrapStaticProps,
} from 'util/wrapProps';
import { weSkiDestinationId } from 'components/common/PlanYourTrip/WeSki/resolver';
import WeSkiPage from 'components/common/PlanYourTrip/WeSki/WeSkiPage';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { getLocale } from 'i18n/locale';
import { isResortWithoutWeSki } from 'util/isResortWithoutWeSki';

export type ResortBookTripProps = {
  resort: IResortPageInfo,
  destinationId: string,
  accommodations: IAccommodationPage | null,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-book-trip-page';
export const TEMPLATE_ID = '3.2.9';
export const TEMPLATE_NAME = 'Resort Book Trip';

export default function ResortBookTrip({ resort, destinationId, pathInfo }: ResortBookTripProps) {
  // const { t } = useOTSTranslation('common');
  const router = useRouter();
  const locale = getLocale();
  const shouldHideWeSki = isResortWithoutWeSki(resort.uuid, locale);
  const redirectUrl = Routes.resortTripPlanningOverview(pathInfo);

  useEffect(() => {
    if (shouldHideWeSki) {
      router.replace(redirectUrl);
    }
  }, [redirectUrl, router, shouldHideWeSki]);

  if (shouldHideWeSki) {
    return null;
  }

  return (
    <WeSkiPage
      destinationId={destinationId}
      focusImage={resort?.image}
    />
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortBookTripProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const fetchedResort = await fetchResortShortInfo(uuid);

  const destinationId = weSkiDestinationId({ resortId: uuid });
  if (!destinationId) {
    throw new NotFoundError('No WeSki destination id');
  }

  return {
    type: 'resort',
    route: 'book-trip',
    pathInfo: cache.data,
    destinationId,
    resort: fetchedResort,
    shortWeather: await fetchResortShortWeatherReport(uuid),

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.BOOK_TRIP,
      tab: CurrentTab.BOOK_TRIP,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort lodging',
    hero: {
      title: 'resort.lodging.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.lodging.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.lodging.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    accommodations: await fetchResortAccommodations(uuid),
  };
};

// This function gets called at build time
export const getResortBookTripStaticProps = wrapStaticProps(fetchProps);
export const getResortBookTripServerSideProps = wrapServerSideProps(fetchProps);
